import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  TextField, Box, Typography, MenuItem, Autocomplete, Alert,
} from '@mui/material';
import DobPicker from '../../components/DobPicker';
import { RootState, AppDispatch } from '../../store/store';
import { setActiveStep, updateFormData, updateFiles } from './onboardingSlice';
import FileUpload from '../../components/FileUpload';
import StepNav from './StepNav';
import { fetchCountries } from '../../api/meta';
import { ID_TYPES, dialCodeOptions, parseDialCode, normalizeDialCode, isAdultDob, requiresIdBack } from './kybConstants';

interface Country { id: string; name: string; }

const OwnerDetails = () => {
  const dispatch: AppDispatch = useDispatch();
  const { formData, activeStep, files } = useSelector((state: RootState) => state.onboarding);
  const parsed = parseDialCode(formData.phone);

  const [phoneCountryCode, setPhoneCountryCode] = useState(normalizeDialCode(formData.phoneCountryCode || parsed.dialCode) || '+44');
  const [phoneNumber, setPhoneNumber] = useState(formData.ownerPhoneLocal || parsed.localNumber);
  const [firstName, setFirstName] = useState(formData.contactFirstName);
  const [lastName, setLastName] = useState(formData.contactLastName);
  const [dob, setDob] = useState(formData.ownerDob);
  const [email, setEmail] = useState(formData.email);
  const [nationality, setNationality] = useState<Country | null>(null);
  const [additionalCitizenships, setAdditionalCitizenships] = useState<Country[]>([]);
  const [street, setStreet] = useState(formData.ownerStreet);
  const [streetLine2, setStreetLine2] = useState(formData.ownerStreetLine2);
  const [city, setCity] = useState(formData.ownerCity);
  const [province, setProvince] = useState(formData.ownerProvince);
  const [zip, setZip] = useState(formData.ownerZip);
  const [country, setCountry] = useState<Country | null>(null);
  const [idType, setIdType] = useState(formData.ownerIdType);
  const [idNumber, setIdNumber] = useState(formData.ownerIdNumber);
  const [countries, setCountries] = useState<Country[]>([]);
  const [touched, setTouched] = useState(false);
  const [metaError, setMetaError] = useState('');

  const commonAccept = 'application/pdf,image/*';
  const maxBytes = 10 * 1024 * 1024;
  const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  useEffect(() => {
    (async () => {
      try {
        const c = await fetchCountries();
        setCountries(c || []);
        if (formData.ownerNationality) {
          setNationality((c || []).find((co) => co.id === formData.ownerNationality) || null);
        }
        if (formData.ownerCountry) {
          setCountry((c || []).find((co) => co.id === formData.ownerCountry) || null);
        }
        if (formData.ownerAdditionalCitizenships) {
          const codes = formData.ownerAdditionalCitizenships.split(',').map((x) => x.trim()).filter(Boolean);
          setAdditionalCitizenships((c || []).filter((co) => codes.includes(co.id)));
        }
        setMetaError('');
      } catch (_) {
        setMetaError('Could not load countries. Check your connection and refresh.');
      }
    })();
  }, [formData.ownerNationality, formData.ownerCountry, formData.ownerAdditionalCitizenships]);

  const validPhone = Boolean(phoneCountryCode && phoneNumber.trim());
  const validId = Boolean(idType && idNumber.trim());
  const idBackRequired = requiresIdBack(idType);
  const validDocs = Boolean(
    files.ownerPassport && files.ownerProofOfAddress && (!idBackRequired || files.ownerIdBack),
  );
  const valid = Boolean(
    validPhone && firstName.trim() && lastName.trim() && dob && isAdultDob(dob)
    && emailRe.test(email.trim()) && nationality && street.trim() && city.trim() && zip.trim() && country
    && validId && validDocs,
  );

  const missing: string[] = [];
  if (!validPhone) missing.push('Phone number');
  if (!firstName.trim()) missing.push('First name');
  if (!lastName.trim()) missing.push('Last name');
  if (!dob) missing.push('Date of birth');
  else if (!isAdultDob(dob)) missing.push('Owner must be at least 18 years old');
  if (!emailRe.test(email.trim())) missing.push('Valid email address');
  if (!nationality) missing.push('Citizenship (pick from the list)');
  if (!street.trim()) missing.push('Address line 1');
  if (!city.trim()) missing.push('City');
  if (!zip.trim()) missing.push('Postal code');
  if (!country) missing.push('Address country (pick from the list)');
  if (!idType) missing.push('Identification type');
  if (!idNumber.trim()) missing.push('Identification number');
  if (!files.ownerProofOfAddress) missing.push('Proof of address document');
  if (!files.ownerPassport) missing.push('ID front image');
  if (idBackRequired && !files.ownerIdBack) missing.push('ID back image');

  const handleNext = () => {
    setTouched(true);
    if (!valid) return;
    const fullPhone = `${phoneCountryCode}${phoneNumber.replace(/\D/g, '')}`;
    dispatch(updateFormData({
      phoneCountryCode,
      ownerPhoneLocal: phoneNumber.replace(/\D/g, ''),
      phone: fullPhone,
      contactFirstName: firstName.trim(),
      contactLastName: lastName.trim(),
      ownerDob: dob,
      email: email.trim(),
      ownerNationality: nationality?.id || '',
      ownerAdditionalCitizenships: additionalCitizenships.map((c) => c.id).join(','),
      ownerStreet: street,
      ownerStreetLine2: streetLine2,
      ownerCity: city,
      ownerProvince: province,
      ownerZip: zip,
      ownerCountry: country?.id || '',
      ownerIdType: idType,
      ownerIdNumber: idNumber.trim(),
    }));
    dispatch(setActiveStep(activeStep + 1));
  };

  return (
    <>
      <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>Owner details for compliance verification.</Typography>

      {metaError && <Alert severity="error" sx={{ mb: 1 }}>{metaError}</Alert>}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 2fr' }, gap: 2 }}>
        <TextField select label="Phone Country Code" value={phoneCountryCode} onChange={(e) => setPhoneCountryCode(e.target.value)} required error={touched && !phoneCountryCode}>
          {dialCodeOptions(phoneCountryCode).map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
        </TextField>
        <TextField label="Phone Number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value.replace(/\D/g, ''))} required error={touched && !phoneNumber.trim()} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        <TextField label="First Name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required error={touched && !firstName.trim()} />
        <TextField label="Last Name" value={lastName} onChange={(e) => setLastName(e.target.value)} required error={touched && !lastName.trim()} />
      </Box>

      <DobPicker
        value={dob}
        onChange={setDob}
        required
        error={touched && (!dob || !isAdultDob(dob))}
        helperText={touched && dob && !isAdultDob(dob) ? 'Must be at least 18 years old' : (touched && !dob ? 'Date of birth is required' : '')}
      />
      <TextField label="Email Address" type="email" fullWidth margin="normal" value={email} onChange={(e) => setEmail(e.target.value)} required error={touched && !emailRe.test(email.trim())} />

      <Autocomplete options={countries} getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} value={nationality} onChange={(_e, v) => setNationality(v)} renderInput={(params) => <TextField {...params} label="Citizenship" margin="normal" required error={touched && !nationality} helperText={touched && !nationality ? 'Select a country from the list' : ''} />} />
      <Autocomplete multiple options={countries.filter((c) => c.id !== nationality?.id)} getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} value={additionalCitizenships} onChange={(_e, v) => setAdditionalCitizenships(v.slice(0, 10))} renderInput={(params) => <TextField {...params} label="Additional Citizenships (optional, up to 10)" margin="normal" />} />

      <Typography variant="subtitle2" sx={{ mt: 2 }}>Owner address</Typography>
      <TextField label="Address Line 1" fullWidth margin="normal" value={street} onChange={(e) => setStreet(e.target.value)} required error={touched && !street.trim()} />
      <TextField label="Address Line 2" fullWidth margin="normal" value={streetLine2} onChange={(e) => setStreetLine2(e.target.value)} />
      <TextField label="City" fullWidth margin="normal" value={city} onChange={(e) => setCity(e.target.value)} required error={touched && !city.trim()} />
      <TextField label="State / Province" fullWidth margin="normal" value={province} onChange={(e) => setProvince(e.target.value)} />
      <TextField label="Postal Code" fullWidth margin="normal" value={zip} onChange={(e) => setZip(e.target.value)} required error={touched && !zip.trim()} />
      <Autocomplete options={countries} getOptionLabel={(o) => o.name || ''} isOptionEqualToValue={(a, b) => a.id === b.id} value={country} onChange={(_e, v) => setCountry(v)} renderInput={(params) => <TextField {...params} label="Address Country" margin="normal" required error={touched && !country} helperText={touched && !country ? 'Select a country from the list' : ''} />} />

      <Box sx={{ mt: 2 }}>
        <FileUpload label="Proof of Address Document" onFileSelect={(f) => dispatch(updateFiles({ ownerProofOfAddress: f }))} selectedFile={files.ownerProofOfAddress} accept={commonAccept} maxBytes={maxBytes} helperText="Utility bill or bank statement" required error={touched && !files.ownerProofOfAddress ? 'Proof of address is required' : null} />
      </Box>

      <TextField select label="Identification Type" fullWidth margin="normal" value={idType} onChange={(e) => setIdType(e.target.value)} required error={touched && !idType}>
        <MenuItem value="">Please select...</MenuItem>
        {ID_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
      </TextField>
      <TextField label="Identification Number" fullWidth margin="normal" value={idNumber} onChange={(e) => setIdNumber(e.target.value)} required error={touched && !idNumber.trim()} />

      <Box sx={{ mt: 2 }}>
        <FileUpload label="Identification Front Image" onFileSelect={(f) => dispatch(updateFiles({ ownerPassport: f }))} selectedFile={files.ownerPassport} accept={commonAccept} maxBytes={maxBytes} required error={touched && !files.ownerPassport ? 'ID front image is required' : null} />
      </Box>
      <Box sx={{ mt: 1 }}>
        <FileUpload
          label={idBackRequired ? 'Back of Government-issued Photo ID' : 'Back of Government-issued Photo ID (optional for passport)'}
          onFileSelect={(f) => dispatch(updateFiles({ ownerIdBack: f }))}
          selectedFile={files.ownerIdBack}
          accept={commonAccept}
          maxBytes={maxBytes}
          required={idBackRequired}
          error={touched && idBackRequired && !files.ownerIdBack ? 'ID back is required for Driver License, National ID, and Residence Permit' : null}
        />
      </Box>

      <StepNav onBack={() => dispatch(setActiveStep(activeStep - 1))} onNext={handleNext} missing={missing} showMissing={touched} />
    </>
  );
};

export default OwnerDetails;
